import {
  type MutableRefObject,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
} from 'react'
import { useSelector } from 'react-redux'
import type { CrmChatProviderKey } from '@/api/types/crm/crmLeadFunnels.types'
import { sortCrmIntegrationMessagesAsc, hasNewIncomingCrmIntegrationMessages } from '../lib/crmIntegrationMessagesMerge'
import { isCrmIntegrationAutoMarkReadProvider } from '../lib/crmChatProviderKey'
import {
  isServerMessageHiddenByPending,
  mapServerMessageToDisplay,
  pendingToDisplayMessage,
  prunePendingAgainstServer,
} from '../lib/crmIntegrationPendingMessages'
import type { CrmIntegrationMessageDisplay } from '../types/crm-integration-message.types'
import { useCrmIntegrationBrowserTabVisibleMessagesRefetch } from './useCrmIntegrationBrowserTabVisibleMessagesRefetch'
import { useCrmIntegrationLoadOlderMessages } from './useCrmIntegrationLoadOlderMessages'
import { useCrmIntegrationMarkRead } from './useCrmIntegrationMarkRead'
import {
  useCrmIntegrationMessagesLoad,
  type CrmIntegrationMessagesLoadStatus,
} from './useCrmIntegrationMessagesLoad'
import { useCrmIntegrationRealtimeMessages } from './useCrmIntegrationRealtimeMessages'
import { useCrmIntegrationSendMessage } from './useCrmIntegrationSendMessage'

export type UseCrmIntegrationChatDataOptions = {
  provider: CrmChatProviderKey
  conversationId?: number | null
  enabled?: boolean
  scrollContainerRef: MutableRefObject<HTMLDivElement | null>
  onMarkReadSuccess?: (leadId: number, conversationId?: number) => void
}

export type UseCrmIntegrationChatDataResult = {
  status: CrmIntegrationMessagesLoadStatus
  conversationTitle: string
  unreadCount: number
  /** Wazzup: тип канала (whatsapp / telegram / instagram / …). */
  externalChatType: string | null
  messages: CrmIntegrationMessageDisplay[]
  errorMessage: string | null
  isSending: boolean
  sendMessage: (text: string, file?: File | null) => Promise<void>
  sendVoiceMessage: (
    file: File,
    previewUrl: string,
    durationSec?: number | null,
  ) => Promise<void>
  retrySendMessage: (clientId: string) => Promise<void>
  loadOlderMessages: () => Promise<void>
  isLoadingOlder: boolean
  hasMoreOlder: boolean
  markDialogRead: () => Promise<void>
  isMarkingRead: boolean
  hasConversation: boolean
  loadMessages: (loadOptions?: { silent?: boolean }) => Promise<void>
}

const getConversationTitle = (
  title: string | null | undefined,
  recipient: string | null | undefined,
  leadId: number,
): string => {
  const normalizedTitle = title?.trim()
  if (normalizedTitle) {
    return normalizedTitle
  }
  const normalizedRecipient = recipient?.trim()
  if (normalizedRecipient) {
    return normalizedRecipient
  }
  return `Лид #${leadId}`
}

export const useCrmIntegrationChatData = (
  leadId: number,
  options: UseCrmIntegrationChatDataOptions,
): UseCrmIntegrationChatDataResult => {
  const enabled = options.enabled !== false
  const conversationId =
    options.conversationId != null && Number.isFinite(Number(options.conversationId))
      ? Number(options.conversationId)
      : null

  const scrollContainerRef = options.scrollContainerRef
  const markInitialPageLoadedRef = useRef<(messageCount: number) => void>(() => {})
  const markDialogReadRef = useRef<
    (
      targetLeadId: number,
      targetConversationId: number | null,
      unreadCount?: number,
    ) => Promise<void>
  >(async () => {})

  const autoMarkRead = isCrmIntegrationAutoMarkReadProvider(options.provider)

  const profile = useSelector(
    (state: { profile?: { profile?: { id?: number | string } } }) =>
      state.profile?.profile,
  )
  const managerUserId =
    profile?.id != null && Number.isFinite(Number(profile.id))
      ? Number(profile.id)
      : undefined

  const {
    status,
    conversation,
    setConversation,
    serverMessages,
    setServerMessages,
    errorMessage,
    loadMessages,
  } = useCrmIntegrationMessagesLoad({
    conversationId,
    enabled,
    onServerMessagesUpdated: (next, context) => {
      if (!context.silent) {
        markInitialPageLoadedRef.current(context.initialPageMessageCount)
      }

      if (
        autoMarkRead &&
        hasNewIncomingCrmIntegrationMessages(context.previousMessages, next)
      ) {
        void markDialogReadRef.current(
          leadId,
          conversationId,
          Math.max(context.conversation?.unread_count ?? 1, 1),
        )
      }
    },
  })

  const syncedReadConversationRef = useRef<number | null>(null)

  useEffect(() => {
    syncedReadConversationRef.current = null
  }, [conversationId, leadId])

  const notifyMarkReadSuccess = useCallback(
    (targetLeadId: number, targetConversationId?: number) => {
      if (targetConversationId != null) {
        syncedReadConversationRef.current = targetConversationId
      }
      options.onMarkReadSuccess?.(targetLeadId, targetConversationId)
    },
    [options.onMarkReadSuccess],
  )

  const resetLocalUnreadCount = useCallback(() => {
    setConversation((prev) => (prev ? { ...prev, unread_count: 0 } : prev))
  }, [setConversation])

  const { markDialogRead, isMarkingRead } = useCrmIntegrationMarkRead({
    resetLocalUnreadCount,
    onMarkReadSuccess: notifyMarkReadSuccess,
  })

  markDialogReadRef.current = markDialogRead

  const {
    pendingMessages,
    setPendingMessages,
    isSending,
    sendMessage,
    sendVoiceMessage,
    retrySendMessage,
    resetSendState,
    confirmOutgoingDelivery,
    handleOutgoingDeliveryFailed,
  } = useCrmIntegrationSendMessage({
    conversationId,
    provider: options.provider,
    managerUserId,
    loadMessages,
    wazzupChannelType: conversation?.external_chat_type ?? null,
  })

  const {
    loadOlderMessages,
    isLoadingOlder,
    hasMoreOlder,
    resetOlderPagination,
    markInitialPageLoaded,
  } = useCrmIntegrationLoadOlderMessages({
    conversationId,
    setServerMessages,
    scrollContainerRef,
  })

  markInitialPageLoadedRef.current = markInitialPageLoaded

  const patchServerMessageDelivery = useCallback(
    (
      messageId: number,
      patch: {
        status: 'sent' | 'failed' | 'pending'
        error?: string | null
        deliveredAt?: string | null
      },
    ) => {
      setServerMessages((prev) =>
        prev.map((message) =>
          message.id === messageId
            ? {
                ...message,
                delivery: {
                  ...message.delivery,
                  status: patch.status,
                  error: patch.error ?? message.delivery.error,
                  deliveredAt:
                    patch.deliveredAt !== undefined
                      ? patch.deliveredAt
                      : message.delivery.deliveredAt,
                },
              }
            : message,
        ),
      )
    },
    [setServerMessages],
  )

  const handleOutgoingMessageDelivered = useCallback(
    (messageId: number) => {
      patchServerMessageDelivery(messageId, {
        status: 'sent',
        error: null,
      })
      confirmOutgoingDelivery(messageId)
    },
    [confirmOutgoingDelivery, patchServerMessageDelivery],
  )

  const handleOutgoingMessageNotDelivered = useCallback(
    ({ messageId, error }: { messageId: number; error: string }) => {
      patchServerMessageDelivery(messageId, {
        status: 'failed',
        error,
      })
      handleOutgoingDeliveryFailed(messageId, error)
    },
    [handleOutgoingDeliveryFailed, patchServerMessageDelivery],
  )

  useCrmIntegrationRealtimeMessages({
    leadId,
    conversationId,
    provider: options.provider,
    enabled,
    loadMessages,
    onIncomingMessage: autoMarkRead
      ? () => {
          void markDialogReadRef.current(
            leadId,
            conversationId,
            Math.max(conversation?.unread_count ?? 1, 1),
          )
        }
      : undefined,
    onConversationRead: resetLocalUnreadCount,
    onOutgoingMessageDelivered: handleOutgoingMessageDelivered,
    onOutgoingMessageNotDelivered: handleOutgoingMessageNotDelivered,
  })

  useCrmIntegrationBrowserTabVisibleMessagesRefetch({
    enabled,
    loadMessages,
  })

  const displayMessages = useMemo((): CrmIntegrationMessageDisplay[] => {
    const serverViews = serverMessages
      .filter(
        (message) => !isServerMessageHiddenByPending(message, pendingMessages),
      )
      .map(mapServerMessageToDisplay)
    const pendingViews = pendingMessages.map(pendingToDisplayMessage)
    return sortCrmIntegrationMessagesAsc([...serverViews, ...pendingViews])
  }, [pendingMessages, serverMessages])

  useEffect(() => {
    if (!enabled) {
      resetSendState()
      resetOlderPagination()
    }
  }, [enabled, resetOlderPagination, resetSendState])

  useLayoutEffect(() => {
    if (serverMessages.length === 0) {
      return
    }

    setPendingMessages((prev) => {
      if (prev.length === 0) {
        return prev
      }

      const next = prunePendingAgainstServer(serverMessages, prev)
      return next.length === prev.length ? prev : next
    })
  }, [serverMessages, setPendingMessages])

  useEffect(() => {
    if (!autoMarkRead) {
      return
    }

    if (status !== 'ready' || conversation == null || conversationId == null) {
      return
    }

    if (conversation.unread_count > 0) {
      void markDialogRead(leadId, conversationId, conversation.unread_count)
      return
    }

    // Диалог уже read, а список/бейджи слева могли остаться со старым unread.
    if (syncedReadConversationRef.current === conversationId) {
      return
    }
    syncedReadConversationRef.current = conversationId
    notifyMarkReadSuccess(leadId, conversationId)
  }, [
    autoMarkRead,
    conversation,
    conversationId,
    leadId,
    markDialogRead,
    notifyMarkReadSuccess,
    status,
  ])

  const markAllAsRead = useCallback(
    () => markDialogRead(leadId, conversationId, conversation?.unread_count ?? 0),
    [conversation?.unread_count, conversationId, leadId, markDialogRead],
  )

  return {
    status,
    conversationTitle: getConversationTitle(
      conversation?.title,
      conversation?.external_recipient,
      leadId,
    ),
    unreadCount: conversation?.unread_count ?? 0,
    externalChatType: conversation?.external_chat_type ?? null,
    messages: displayMessages,
    errorMessage,
    isSending,
    sendMessage,
    sendVoiceMessage,
    retrySendMessage,
    loadOlderMessages,
    isLoadingOlder,
    hasMoreOlder,
    markDialogRead: markAllAsRead,
    isMarkingRead,
    hasConversation: conversation != null,
    loadMessages,
  }
}
